import { useState, useEffect } from "react";
import axios from "axios";
import "./BuildingTypeManagement.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

function BuildingTypeManagement() {
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [luseList, setLuseList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    applicableZoning: [],
  });
  const [newBuildingType, setNewBuildingType] = useState({
    code: "",
    name: "",
    description: "",
    applicableZoning: [],
  });

  useEffect(() => {
    fetchBuildingTypes();
    fetchLuseList();
  }, []);

  const fetchBuildingTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/building-types`);
      setBuildingTypes(response.data);
    } catch (error) {
      console.error("Error fetching building types:", error);
      alert("Failed to load building types.");
    } finally {
      setLoading(false);
    }
  };

  const fetchLuseList = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/dubai/zoning/luse-list`);
      setLuseList(response.data || []);
    } catch (error) {
      console.error("Error fetching luse list:", error);
      alert("Failed to load zoning list.");
    }
  };

  const handleZoningToggle = (luse, isEditMode = false) => {
    if (isEditMode) {
      const currentZoning = editForm.applicableZoning || [];
      if (currentZoning.includes(luse)) {
        setEditForm({
          ...editForm,
          applicableZoning: currentZoning.filter((z) => z !== luse),
        });
      } else {
        setEditForm({
          ...editForm,
          applicableZoning: [...currentZoning, luse],
        });
      }
    } else {
      const currentZoning = newBuildingType.applicableZoning || [];
      if (currentZoning.includes(luse)) {
        setNewBuildingType({
          ...newBuildingType,
          applicableZoning: currentZoning.filter((z) => z !== luse),
        });
      } else {
        setNewBuildingType({
          ...newBuildingType,
          applicableZoning: [...currentZoning, luse],
        });
      }
    }
  };

  const handleAddBuildingType = async () => {
    if (!newBuildingType.code || !newBuildingType.name) {
      alert("Code and name are required fields.");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        code: newBuildingType.code,
        name: newBuildingType.name,
        description: newBuildingType.description || undefined,
        applicableZoning:
          newBuildingType.applicableZoning.length > 0
            ? newBuildingType.applicableZoning
            : undefined,
      };
      await axios.post(`${API_BASE_URL}/api/admin/building-types`, payload);
      await fetchBuildingTypes();
      setShowAddForm(false);
      setNewBuildingType({
        code: "",
        name: "",
        description: "",
        applicableZoning: [],
      });
      alert("Building type added.");
    } catch (error) {
      console.error("Error adding building type:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to add building type.";
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (type) => {
    setEditingCode(type.code);
    setEditForm({
      name: type.name || "",
      description: type.description || "",
      applicableZoning: type.applicableZoning || [],
    });
  };

  const handleCancelEdit = () => {
    setEditingCode(null);
    setEditForm({
      name: "",
      description: "",
      applicableZoning: [],
    });
  };

  const handleSaveEdit = async (code) => {
    if (!editForm.name) {
      alert("Name is a required field.");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: editForm.name,
        description: editForm.description || undefined,
        applicableZoning:
          editForm.applicableZoning.length > 0
            ? editForm.applicableZoning
            : undefined,
      };
      await axios.patch(`${API_BASE_URL}/api/admin/building-types/${code}`, payload);
      await fetchBuildingTypes();
      setEditingCode(null);
      setEditForm({
        name: "",
        description: "",
        applicableZoning: [],
      });
      alert("Update completed.");
    } catch (error) {
      console.error("Error updating building type:", error);
      const errorMessage =
        error.response?.data?.message || "Update failed.";
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBuildingType = async (code) => {
    if (
      !window.confirm(
        `Are you sure you want to delete building type "${code}"?\nAll related work type costs will also be deleted.`
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API_BASE_URL}/api/admin/building-types/${code}`);
      await fetchBuildingTypes();
      alert("Building type deleted.");
    } catch (error) {
      console.error("Error deleting building type:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to delete building type.";
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="building-type-management">
      <div className="page-header">
        <h1>Building Type Management</h1>
        <button
          className="btn btn-primary"
          onClick={() => {
            if (editingCode) {
              handleCancelEdit();
            }
            setShowAddForm(!showAddForm);
          }}
          disabled={loading}
        >
          {showAddForm ? "Cancel" : "+ Add Building Type"}
        </button>
      </div>

      {editingCode && (
        <div className="add-form edit-form">
          <h3>Edit Building Type</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>
                Code <span className="required">*</span>
              </label>
              <input
                type="text"
                value={editingCode}
                className="form-input readonly"
                readOnly
                disabled
              />
              <small className="form-hint">Code cannot be modified.</small>
            </div>
            <div className="form-group">
              <label>
                Name <span className="required">*</span>
              </label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm({ ...editForm, name: e.target.value })
                }
                className="form-input"
                placeholder="例如：獨棟住宅、公寓"
              />
            </div>
            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                value={editForm.description}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    description: e.target.value,
                  })
                }
                className="form-input"
                rows="3"
                placeholder="Enter building type description"
              />
            </div>
            <div className="form-group full-width">
              <label>Applicable Zoning</label>
              <div className="zoning-checkbox-container">
                {luseList.length === 0 ? (
                  <div className="loading-zoning">Loading zoning list...</div>
                ) : (
                  <div className="zoning-checkbox-grid">
                    {luseList.map((luse) => (
                      <label key={luse} className="zoning-checkbox-label">
                        <input
                          type="checkbox"
                          checked={editForm.applicableZoning.includes(luse)}
                          onChange={() => handleZoningToggle(luse, true)}
                          className="zoning-checkbox"
                        />
                        <span>{luse}</span>
                      </label>
                    ))}
                  </div>
                )}
                {editForm.applicableZoning.length > 0 && (
                  <div className="selected-zoning">
                    <strong>Selected Zoning ({editForm.applicableZoning.length}):</strong>
                    <div className="selected-zoning-tags">
                      {editForm.applicableZoning.map((zoning) => (
                        <span key={zoning} className="selected-zoning-tag">
                          {zoning}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button
              className="btn btn-primary"
              onClick={() => handleSaveEdit(editingCode)}
              disabled={loading}
            >
              Save
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleCancelEdit}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showAddForm && (
        <div className="add-form">
          <h3>Add Building Type</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>
                Code <span className="required">*</span>
              </label>
              <input
                type="text"
                value={newBuildingType.code}
                onChange={(e) =>
                  setNewBuildingType({
                    ...newBuildingType,
                    code: e.target.value.toUpperCase(),
                  })
                }
                className="form-input"
                placeholder="예: DETACHED, APARTMENT"
              />
            </div>
            <div className="form-group">
              <label>
                Name <span className="required">*</span>
              </label>
              <input
                type="text"
                value={newBuildingType.name}
                onChange={(e) =>
                  setNewBuildingType({
                    ...newBuildingType,
                    name: e.target.value,
                  })
                }
                className="form-input"
                placeholder="例如：獨棟住宅、公寓"
              />
            </div>
            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                value={newBuildingType.description}
                onChange={(e) =>
                  setNewBuildingType({
                    ...newBuildingType,
                    description: e.target.value,
                  })
                }
                className="form-input"
                rows="3"
                placeholder="Enter building type description"
              />
            </div>
            <div className="form-group full-width">
              <label>Applicable Zoning</label>
              <div className="zoning-checkbox-container">
                {luseList.length === 0 ? (
                  <div className="loading-zoning">Loading zoning list...</div>
                ) : (
                  <div className="zoning-checkbox-grid">
                    {luseList.map((luse) => (
                      <label key={luse} className="zoning-checkbox-label">
                        <input
                          type="checkbox"
                          checked={newBuildingType.applicableZoning.includes(luse)}
                          onChange={() => handleZoningToggle(luse)}
                          className="zoning-checkbox"
                        />
                        <span>{luse}</span>
                      </label>
                    ))}
                  </div>
                )}
                {newBuildingType.applicableZoning.length > 0 && (
                  <div className="selected-zoning">
                    <strong>Selected Zoning ({newBuildingType.applicableZoning.length}):</strong>
                    <div className="selected-zoning-tags">
                      {newBuildingType.applicableZoning.map((zoning) => (
                        <span key={zoning} className="selected-zoning-tag">
                          {zoning}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleAddBuildingType}>
              Add
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowAddForm(false);
                setNewBuildingType({
                  code: "",
                  name: "",
                  description: "",
                  applicableZoning: [],
                });
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="building-types-table-wrapper">
          <table className="building-types-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Description</th>
                <th>Applicable Zoning</th>
                <th>Work Types</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {buildingTypes.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-state">
                    No registered building types.
                  </td>
                </tr>
              ) : (
                buildingTypes.map((type) => (
                  <tr key={type.code}>
                    <td className="code-cell">
                      <strong>{type.code}</strong>
                    </td>
                    <td>{type.name}</td>
                    <td>{type.description || "-"}</td>
                    <td>
                      {type.applicableZoning &&
                      type.applicableZoning.length > 0 ? (
                        <div className="zoning-tags">
                          {type.applicableZoning.map((zoning, index) => (
                            <span key={index} className="zoning-tag">
                              {zoning}
                            </span>
                          ))}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="count-cell">
                      {type._count?.costs || 0}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(type)}
                          title="Edit"
                          disabled={loading || editingCode !== null || showAddForm}
                        >
                          ✎
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDeleteBuildingType(type.code)}
                          title="Delete"
                          disabled={loading || editingCode !== null || showAddForm}
                        >
                          🗑
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default BuildingTypeManagement;

